import type { ReactNode } from 'react'
import { Avatar, Button } from '@mantine/core'
import { IconCheck } from '@tabler/icons-react'
import { avatarColors, initials } from './briefingCopy'

/** Patient photo, else initials on the patient's own soft color. */
export function PatientAvatar({
  name,
  photoUrl,
  size,
}: {
  name: string | null
  photoUrl: string | null | undefined
  size: number
}) {
  const { bg, fg } = avatarColors(name)
  return (
    <Avatar
      src={photoUrl}
      radius="xl"
      size={size}
      styles={{ placeholder: { background: bg, color: fg, fontWeight: 700 } }}
      title={name ?? undefined}
    >
      {initials(name)}
    </Avatar>
  )
}

// Inline styles: Mantine's button colors are not layered, so Tailwind classes lose to them.
const QUICK_STYLES = {
  root: {
    background: '#fff1ee',
    borderColor: '#f3c9bf',
    color: '#b23a24',
    height: 26,
    paddingInline: 9,
    fontSize: 12,
    fontWeight: 600,
  },
} as const

/** The coral in-place action ("✓ Mark reviewed", "✓ Resend link"). */
export function QuickButton({
  children,
  icon,
  loading,
  disabled,
  onClick,
}: {
  children: ReactNode
  icon?: ReactNode
  loading?: boolean
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <Button
      variant="default"
      size="compact-xs"
      radius="md"
      styles={QUICK_STYLES}
      leftSection={icon ?? <IconCheck size={12} stroke={2.5} />}
      loading={loading}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  )
}

/** "✓ Reviewed · Undo" after an in-place action. */
export function DoneLine({ did, onUndo }: { did: string; onUndo: (() => void) | null }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#15803d]">
      <IconCheck size={13} stroke={2.5} />
      {did}
      {onUndo && (
        <>
          <span aria-hidden>·</span>
          <Button variant="subtle" size="compact-xs" px={2} fz={12} fw={500} onClick={onUndo}>
            Undo
          </Button>
        </>
      )}
    </span>
  )
}
